<div class="finance-list-page" *ngIf="canViewPayables">
    <app-page-shell>
        <p-toast position="top-center"></p-toast>
        <p-progressBar mode="indeterminate" *ngIf="isLoading"></p-progressBar>

        <app-page-hero [eyebrow]="t('navigation.payables')" [title]="t('navigation.account_payables')"
            subtitle="Review payable staging lines and generate vendor bills while preserving the current behavior.">
            <div actions>
                <p-button *ngIf="canGenerateBill && !isConsolidatedReadOnly" [label]="generateBillLabel" icon="pi pi-file"
                    (click)="generateBill()" [disabled]="!canGenerateSelectedBill"
                    [pTooltip]="billSelectionBlocker || (checkingBillSelection ? 'Checking purchase order readiness' : '')"></p-button>
                <p-button *ngIf="canGenerateBill && !isConsolidatedReadOnly" label="Manual Payable" icon="pi pi-plus"
                    severity="secondary" [outlined]="true" (click)="createManualPayable()"></p-button>
                <p-button *ngIf="canExportPayables" label="Export" icon="pi pi-file-excel"
                    severity="secondary" [outlined]="true" (click)="exportToExcel()"></p-button>
            </div>
        </app-page-hero>

        <app-page-data-panel *ngIf="createdBillDrafts.length > 1" title="Created bill drafts" subtitle="Drafts are grouped by vendor and currency. Open a draft to continue.">
            <div class="bulk-actions">
                <p-button *ngFor="let bill of createdBillDrafts" [label]="bill.billNumber || 'Open bill draft'" icon="pi pi-file" severity="secondary" [outlined]="true" (click)="openCreatedBill(bill)"></p-button>
            </div>
        </app-page-data-panel>

        <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

        <app-page-search-panel title="Search payables" [activeFilters]="activeFilterCount"
            [resultText]="resultText" [collapsed]="true" (search)="applyFilters()" (clear)="clearFilters()">
            <div class="bulk-actions" style="margin-bottom: .75rem;">
                <p-button *ngFor="let view of savedFilterViews" [label]="view.label" size="small" severity="secondary"
                    [outlined]="true" (click)="applySavedFilter(view)"></p-button>
            </div>
            <div class="finance-search-grid">
                <app-company-scope-selector (scopeChange)="onScopeChange($event)"></app-company-scope-selector>

                <div class="field">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-users"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <p-multiSelect inputId="client" [options]="clients" [(ngModel)]="selectedClients"
                                optionLabel="name" display="chip" [showClear]="true" styleClass="w-full"
                                (onChange)="onSelectedClientsChange()"></p-multiSelect>
                            <label for="client">CRM Vendors</label>
                        </p-iftalabel>
                    </p-inputGroup>
                </div>

                <div class="field">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-address-book"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <p-multiSelect inputId="contact" [options]="contacts" [(ngModel)]="selectedContacts"
                                optionLabel="displayName" display="chip" [showClear]="true" styleClass="w-full"
                                [disabled]="!selectedClients.length"></p-multiSelect>
                            <label for="contact">CRM Contacts</label>
                        </p-iftalabel>
                    </p-inputGroup>
                </div>

                <div class="field">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-calendar"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <p-datePicker inputId="dateRange" [(ngModel)]="dateRange" selectionMode="range"
                                [readonlyInput]="true" dateFormat="yy-mm-dd" appendTo="body"
                                styleClass="w-full"></p-datePicker>
                            <label for="dateRange">Billing Date Range</label>
                        </p-iftalabel>
                    </p-inputGroup>
                </div>

                <div class="field">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-tag"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <p-multiSelect inputId="status" [options]="statuses" [(ngModel)]="selectedStatuses"
                                display="chip" [showClear]="true" styleClass="w-full"></p-multiSelect>
                            <label for="status">Status</label>
                        </p-iftalabel>
                    </p-inputGroup>
                </div>

                <div class="field">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-pencil"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <p-multiSelect inputId="sourceType" [options]="sourceTypes" [(ngModel)]="selectedSourceTypes"
                                display="chip" [showClear]="true" styleClass="w-full"></p-multiSelect>
                            <label for="sourceType">Source Type</label>
                        </p-iftalabel>
                    </p-inputGroup>
                </div>

                <div class="field">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-hashtag"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <input id="reference" type="text" pInputText [(ngModel)]="referenceFilter" />
                            <label for="reference">Reference #</label>
                        </p-iftalabel>
                    </p-inputGroup>
                </div>

                <div class="field">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-link"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <input id="sourceReference" type="text" pInputText [(ngModel)]="sourceReferenceFilter" />
                            <label for="sourceReference">Source Reference</label>
                        </p-iftalabel>
                    </p-inputGroup>
                </div>

                <div class="field">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-file"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <input id="billNumber" type="text" pInputText [(ngModel)]="billNumberFilter" />
                            <label for="billNumber">Bill #</label>
                        </p-iftalabel>
                    </p-inputGroup>
                </div>
            </div>
        </app-page-search-panel>

        <app-page-data-panel title="Payable staging" subtitle="Grouped rows replace the previous expanded table."
            [resultText]="resultText">
            <div actions>
                <p-button *ngIf="canExportPayables" label="Export" icon="pi pi-file-excel"
                    severity="secondary" [outlined]="true" (click)="exportToExcel()"></p-button>
            </div>

            <div class="bulk-action-bar" *ngIf="selectedPayables.length && !isConsolidatedReadOnly">
                <div>
                    <strong>{{ selectedPayables.length }}</strong> selected,
                    <strong>{{ formatMoney(getSelectedTotal()) }}</strong>
                    <small *ngIf="billSelectionBlocker" class="block text-red-600 mt-1" role="alert">{{ billSelectionBlocker }}</small>
                </div>
                <div class="bulk-actions">
                    <p-button *ngIf="canGenerateBill" [label]="generateBillLabel" icon="pi pi-file"
                        (click)="generateBill()" [disabled]="!canGenerateSelectedBill"
                        [pTooltip]="billSelectionBlocker || (checkingBillSelection ? 'Checking purchase order readiness' : '')"></p-button>
                    <p-button *ngIf="canEditPayables" label="Hold" icon="pi pi-pause"
                        severity="warn" [outlined]="true" (click)="holdSelected()"></p-button>
                    <p-button *ngIf="canEditPayables" label="Release" icon="pi pi-play"
                        severity="success" [outlined]="true" (click)="releaseSelected()"></p-button>
                    <p-button *ngIf="canEditPayables" label="Submit Approval" icon="pi pi-send"
                        severity="secondary" [outlined]="true" (click)="submitSelectedForApproval()"></p-button>
                    <p-button *ngIf="canGenerateBill" label="Manual Payable" icon="pi pi-plus"
                        severity="secondary" [outlined]="true" (click)="createManualPayable()"></p-button>
                </div>
            </div>

            <app-accounting-directory-controls documentType="PAYABLE" [filters]="directoryFilters"
                [searchText]="searchText" [selectedStatuses]="selectedStatuses" [pendingStatuses]="pendingStatuses" (searchTextChange)="onDirectorySearch($event)"
                (modeChange)="onDirectoryModeChange($event)" (pending)="showPendingStatuses()"
                (all)="showAllStatuses()"></app-accounting-directory-controls>
            <p-table *ngIf="directoryMode === 'LIST'" class="finance-table" [value]="allPayables" dataKey="payableId" [paginator]="true"
                [rows]="rows" [first]="first" [showCurrentPageReport]="true" [totalRecords]="totalRecords"
                [lazy]="true" currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                (onLazyLoad)="loadData($event)" [rowsPerPageOptions]="[20, 50, 100]"
                [(selection)]="selectedPayables" (selectionChange)="onPayableSelectionChange($event)" [tableStyle]="{ 'min-width': '0' }">
                <ng-template pTemplate="header">
                    <tr>
                        <th *ngIf="!isConsolidatedReadOnly" style="width: 3.5rem"><p-tableHeaderCheckbox></p-tableHeaderCheckbox></th>
                        <th>Source/Reference</th>
                        <th>Vendor</th>
                        <th>Payable Amount</th>
                        <th>Readiness/Match Status</th>
                        <th>Dates/Owner</th>
                        <th>Actions</th>
                    </tr>
                </ng-template>
                <ng-template pTemplate="body" let-payable>
                    <tr>
                        <td *ngIf="!isConsolidatedReadOnly" data-label="Select"><p-tableCheckbox [value]="payable"></p-tableCheckbox></td>
                        <td data-label="Source/Reference">
                            <div class="stack-cell">
                                <button type="button" class="cell-title link-button" (click)="viewDetails(payable)">
                                    {{ displayDocumentReference(payable.payableReferenceNumber) }}
                                </button>
                                <div class="cell-meta">
                                    <span class="code-pill">{{ payable.payableTxnType || 'SOURCE' }}</span>
                                    <span *ngIf="payable.billNumber">Bill {{ payable.billNumber }}</span>
                                </div>
                                <ng-container *ngIf="headerSourceReferences(payable) as headerRefs">
                                    <div class="cell-meta" *ngIf="headerRefs.length">
                                        Header: {{ headerRefs.join(', ') }}
                                    </div>
                                </ng-container>
                                <ng-container *ngIf="lineSourceReferences(payable) as lineRefs">
                                    <div class="cell-meta" *ngIf="lineRefs.length">
                                        Lines: {{ lineRefs.slice(0, 2).join(', ') }}
                                        <span *ngIf="lineRefs.length > 2" tabindex="0" [pTooltip]="lineRefs.join(', ')"
                                            [attr.aria-label]="'Lines: ' + lineRefs.join(', ')">+{{ lineRefs.length - 2 }}</span>
                                    </div>
                                </ng-container>
                            </div>
                        </td>
                        <td data-label="Vendor">
                            <div class="stack-cell">
                                <span class="cell-title">{{ crmPartyContext.displayName(payable) }}</span>
                                <div class="cell-meta">Contact: {{ crmPartyContext.contactDisplayName(payable) }}</div>
                            </div>
                        </td>
                        <td data-label="Payable Amount">
                            <div class="amount-block">
                                <span class="amount-primary">{{ payable.amount | currency:payable.country?.currencyCode:(payable.country?.currencyCode || 'SAR') + ' ' }}</span>
                                <span class="amount-secondary">{{ t('accountingAmounts.beforeTax') }} {{ payable.amountExclTax != null ? (payable.amountExclTax | currency:payable.country?.currencyCode:(payable.country?.currencyCode || 'SAR') + ' ') : '—' }}</span>
                                <span class="amount-secondary">Tax {{ payable.taxAmount | currency:payable.country?.currencyCode:(payable.country?.currencyCode || 'SAR') + ' ' }}</span>
                            </div>
                        </td>
                        <td data-label="Readiness/Match Status">
                            <div class="status-stack">
                                <p-tag [severity]="statusSeverity(payable.status)" [value]="payable.status || 'UNKNOWN'"></p-tag>
                                <span class="muted-pill">{{ payable.accountPayablesLines?.length || 0 }} lines</span>
                            </div>
                        </td>
                        <td data-label="Dates/Owner">
                            <div class="stack-cell">
                                <span class="cell-title">{{ payable.billingDate | date:'yyyy-MM-dd' }}</span>
                                <div class="cell-meta vertical">
                                    <span>Created {{ payable.createdAt | date:'yyyy-MM-dd' }}</span>
                                    <span>Expected {{ payable.expectedPaymentDate | date:'yyyy-MM-dd' }}</span>
                                </div>
                            </div>
                        </td>
                        <td data-label="Actions">
                            <div class="row-actions">
                                <p-button icon="pi pi-eye" [rounded]="true" [text]="true" pTooltip="View payable"
                                    (click)="viewPayable(payable)"></p-button>
                                <p-button *ngIf="canEditPayable(payable)" icon="pi pi-pencil" [rounded]="true" [text]="true"
                                    severity="secondary" pTooltip="Edit manual/staged line" (click)="editPayable(payable)"></p-button>
                                <p-button icon="pi pi-external-link" [rounded]="true" [text]="true" pTooltip="View source"
                                    (click)="viewDetails(payable)"></p-button>
                            </div>
                        </td>
                    </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                    <tr>
                        <td colspan="7">
                            <div class="empty-state">
                                <i class="pi pi-inbox"></i>
                                <strong>No payables match these filters</strong>
                                <span>Clear filters or refresh the list to view staged payables.</span>
                            </div>
                        </td>
                    </tr>
                </ng-template>
            </p-table>
        </app-page-data-panel>
    </app-page-shell>

    <p-drawer [(visible)]="isPreviewVisible" position="right" styleClass="finance-preview-drawer">
        <ng-template pTemplate="header">
            <div class="stack-cell">
                <strong>{{ displayDocumentReference(previewPayable?.payableReferenceNumber) }}</strong>
                <span class="cell-meta">{{ previewPayable?.accountPayablesLines?.length || 0 }} lines</span>
            </div>
        </ng-template>
        <div class="preview-list" *ngIf="previewPayable?.accountPayablesLines?.length; else noPayableLines">
            <div class="preview-line" *ngFor="let line of previewPayable?.accountPayablesLines">
                <div class="preview-line-header">
                    <div>
                        <div class="preview-line-title">{{ line.lineDescription || 'Line item' }}</div>
                        <div class="cell-meta">{{ line.payablesLineType || 'MISCELLANEOUS' }} · {{ line.payableLineRefNumber || 'No source reference' }}</div>
                    </div>
                    <div class="preview-line-amount">{{ formatMoney(line.amount, previewPayable?.country?.currencyCode) }}</div>
                </div>
            </div>
        </div>
        <ng-template #noPayableLines>
            <div class="preview-empty">No payable lines available.</div>
        </ng-template>
    </p-drawer>
</div>

<div class="finance-list-page" *ngIf="!canViewPayables">
    <p-toast position="top-center"></p-toast>
    <div class="empty-state">
        <i class="pi pi-lock"></i>
        <strong>Payables are not available</strong>
        <span>You do not have permission to view this page.</span>
    </div>
</div>
